import { useCallback, useEffect, useState, useMemo } from "react";
import { api } from "@/lib/auth";
import { fmtDateTime } from "@/lib/datetime";
import ErrorBoundary from "@/components/ErrorBoundary";
import Photo360Viewer from "@/components/Photo360Viewer";
import AiFlagToggle from "@/components/admin/AiFlagToggle";
import { HFButton } from "@/components/ui-hf";
import {
    Sparkles, Loader2, Download, RefreshCw, Trash2, Info, Compass, Circle,
} from "lucide-react";

/**
 * AdminVorherNachher360 — Skybox-basiertes 360°-Bildpaar-Werkzeug.
 *
 * Nutzt Blockade Labs Skybox mit control_image=remix für Stufe 2, damit die
 * Architektur/Perspektive erhalten bleibt.
 */

const VORHER_OPTIONS = [
    { value: "rohbau", label: "Rohbau" },
    { value: "sanierungsfall", label: "Sanierungsfall" },
    { value: "leerstand", label: "Leerstand" },
    { value: "unrenoviert", label: "Unrenoviert (70er/80er)" },
    { value: "custom", label: "✏️ Eigene Beschreibung…" },
];
const NACHHER_OPTIONS = [
    { value: "fertigstellung_neubau", label: "Fertigstellung Neubau" },
    { value: "premium_renovation", label: "Premium-Renovierung" },
    { value: "editorial_golden_hour", label: "Editorial Golden Hour" },
    { value: "moebliert_bewohnt", label: "Möbliert & bewohnt" },
    { value: "custom", label: "✏️ Eigene Beschreibung…" },
];
const MODEL_OPTIONS = [
    { value: "model_3", label: "Model 3 – Standard", latency: "≈ 20–40 s", cost: "~$0.56 / Paar" },
    { value: "model_4_standard", label: "Model 4 – Standard", latency: "≈ 1–2 min", cost: "~$1.68 / Paar" },
    { value: "model_4_enhanced", label: "Model 4 – Enhanced", latency: "≈ 2–4 min", cost: "~$3.36 / Paar" },
];
const MODEL_LABEL: Record<string, string> = Object.fromEntries(
    MODEL_OPTIONS.map((m) => [m.value, m.label]),
);
const OPTION_LABEL: Record<string, string> = Object.fromEntries(
    [...VORHER_OPTIONS, ...NACHHER_OPTIONS].map((o) => [o.value, o.label]),
);

type PairPart = { media_id: string; url: string; title: string; width?: number; height?: number };
type Pair = {
    id: string;
    objekt: string;
    vorher_szenario: string;
    vorher_custom_text?: string | null;
    nachher_szenario: string;
    nachher_custom_text?: string | null;
    model: string;
    created_at: string;
    admin_email?: string;
    vorher: PairPart | null;
    nachher: PairPart | null;
};

export default function AdminVorherNachher360() {
    return (
        <ErrorBoundary
            testId="admin-vn360-error-boundary"
            title="360°-Vorher-Nachher-Generator konnte nicht geladen werden."
        >
            <Inner />
        </ErrorBoundary>
    );
}

function CustomBox(props: {
    testIdPrefix: string; value: string; onChange: (v: string) => void;
    placeholder: string; disabled?: boolean;
}) {
    const len = props.value.length;
    const bad = (len > 0 && len < 10) || len > 500;
    return (
        <div className="space-y-1">
            <textarea
                value={props.value}
                onChange={(e) => props.onChange(e.target.value)}
                placeholder={props.placeholder}
                className="w-full px-3 py-2 rounded-sm bg-white border text-sm"
                style={{ borderColor: "hsl(var(--hf-line))", minHeight: 72, resize: "vertical" }}
                rows={3} maxLength={600}
                data-testid={`${props.testIdPrefix}-textarea`}
                disabled={props.disabled}
            />
            <div className="flex justify-between text-[11px] text-hf-muted">
                <span>Konkret, panoramische Perspektive beschreiben (DE oder EN).</span>
                <span
                    className={bad ? "text-hf-danger" : ""}
                    data-testid={`${props.testIdPrefix}-counter`}
                >
                    {len} / 500
                </span>
            </div>
        </div>
    );
}

function scenarioBadge(sz: string, customText?: string | null) {
    if (sz === "custom") {
        const t = (customText || "").trim();
        const short = t.length > 60 ? t.slice(0, 60) + "…" : (t || "Eigene Beschreibung");
        return { label: short, full: t || "Eigene Beschreibung" };
    }
    return { label: OPTION_LABEL[sz] || sz, full: OPTION_LABEL[sz] || sz };
}

function Inner() {
    const [objekt, setObjekt] = useState("");
    const [vorherSz, setVorherSz] = useState("sanierungsfall");
    const [nachherSz, setNachherSz] = useState("premium_renovation");
    const [vorherCustom, setVorherCustom] = useState("");
    const [nachherCustom, setNachherCustom] = useState("");
    const [model, setModel] = useState("model_4_enhanced");
    const [loading, setLoading] = useState(false);
    const [progressStage, setProgressStage] = useState<"idle" | "vorher-start" | "vorher-render" | "nachher-start" | "nachher-render">("idle");
    const [elapsedSec, setElapsedSec] = useState(0);
    const [error, setError] = useState("");
    const [current, setCurrent] = useState<Pair | null>(null);
    const [history, setHistory] = useState<Pair[]>([]);
    const [quota, setQuota] = useState<{ used: number; limit: number }>({ used: 0, limit: 10 });
    const [regenerating, setRegenerating] = useState(false);

    const loadHistory = useCallback(async () => {
        const r = await api<{ items: Pair[]; quota: { used: number; limit: number } }>(
            "/api/admin/vorher-nachher-360/history?limit=20",
        );
        if (r.ok && r.data) {
            setHistory(r.data.items);
            setQuota(r.data.quota);
        }
    }, []);
    useEffect(() => { void loadHistory(); }, [loadHistory]);
    useEffect(() => {
        if (!error) return;
        const t = setTimeout(() => setError(""), 10_000);
        return () => clearTimeout(t);
    }, [error]);

    // Live-Elapsed-Timer während loading
    useEffect(() => {
        if (!loading) { setElapsedSec(0); return; }
        const start = Date.now();
        const t = setInterval(() => setElapsedSec(Math.round((Date.now() - start) / 1000)), 1000);
        return () => clearInterval(t);
    }, [loading]);

    // Geschätzte Gesamtzeit pro Modell (2 Stufen)
    const totalEstimateSec = useMemo(() => {
        return model === "model_3" ? 60 : model === "model_4_standard" ? 180 : 300;
    }, [model]);

    // Automatisches Stufen-Umschalten (schätzt basierend auf Latenz — echte
    // Skybox-Status-API wäre schöner, aber gepollt vom Backend, nicht vom
    // Frontend. Wir zeigen ehrlichen Näherungs-Progress.)
    useEffect(() => {
        if (!loading) { setProgressStage("idle"); return; }
        const halfway = totalEstimateSec / 2;
        setProgressStage("vorher-start");
        const t1 = setTimeout(() => setProgressStage("vorher-render"), 5000);
        const t2 = setTimeout(() => setProgressStage("nachher-start"), halfway * 1000);
        const t3 = setTimeout(() => setProgressStage("nachher-render"), (halfway + 5) * 1000);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }, [loading, totalEstimateSec]);

    const generate = useCallback(async () => {
        if (objekt.trim().length < 3) { setError("Bitte das Objekt beschreiben (min. 3 Zeichen)."); return; }
        if (vorherSz === "custom" && vorherCustom.trim().length < 10) {
            setError("Vorher-Beschreibung zu kurz (min. 10 Zeichen)."); return;
        }
        if (nachherSz === "custom" && nachherCustom.trim().length < 10) {
            setError("Nachher-Beschreibung zu kurz (min. 10 Zeichen)."); return;
        }
        setError("");
        setLoading(true);
        const res = await api<Pair & { quota: { used: number; limit: number } }>(
            "/api/admin/vorher-nachher-360/generate",
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    objekt: objekt.trim(),
                    vorher_szenario: vorherSz,
                    vorher_custom_text: vorherSz === "custom" ? vorherCustom.trim() : null,
                    nachher_szenario: nachherSz,
                    nachher_custom_text: nachherSz === "custom" ? nachherCustom.trim() : null,
                    model,
                }),
            },
        );
        setLoading(false);
        if (!res.ok || !res.data) { setError(res.error || "Generierung fehlgeschlagen."); return; }
        setCurrent(res.data);
        if (res.data.quota) setQuota(res.data.quota);
        void loadHistory();
    }, [objekt, vorherSz, nachherSz, vorherCustom, nachherCustom, model, loadHistory]);

    const regenerateNachher = useCallback(async () => {
        if (!current) return;
        setRegenerating(true);
        setError("");
        const res = await api<Pair & { quota: { used: number; limit: number } }>(
            `/api/admin/vorher-nachher-360/${current.id}/regenerate-nachher`,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    nachher_szenario: current.nachher_szenario,
                    nachher_custom_text: current.nachher_szenario === "custom"
                        ? (current.nachher_custom_text || nachherCustom.trim()) : null,
                    model,
                }),
            },
        );
        setRegenerating(false);
        if (!res.ok || !res.data) { setError(res.error || "Nachher-Regeneration fehlgeschlagen."); return; }
        setCurrent(res.data);
        if (res.data.quota) setQuota(res.data.quota);
        void loadHistory();
    }, [current, nachherCustom, model, loadHistory]);

    const deleteEntry = useCallback(async (id: string) => {
        const res = await api(`/api/admin/vorher-nachher-360/${id}`, { method: "DELETE" });
        if (res.ok) {
            if (current?.id === id) setCurrent(null);
            void loadHistory();
        }
    }, [current, loadHistory]);

    const downloadFile = (url: string, filename: string) => {
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.target = "_blank"; a.rel = "noopener";
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
    };

    // Progress-Label
    const stageLabel = useMemo(() => {
        const total = totalEstimateSec;
        const remain = Math.max(0, total - elapsedSec);
        const mins = Math.floor(remain / 60);
        const secs = remain % 60;
        const eta = mins > 0 ? `${mins} min ${secs}s` : `${secs}s`;
        switch (progressStage) {
            case "vorher-start":  return `Skybox-Job gestartet (Vorher, ${MODEL_LABEL[model]}) … ca. ${eta} verbleibend`;
            case "vorher-render": return `Vorher-Panorama rendert … ca. ${eta} verbleibend`;
            case "nachher-start": return `Nachher-Job mit Control-Image gestartet … ca. ${eta} verbleibend`;
            case "nachher-render": return `Nachher-Panorama rendert … ca. ${eta} verbleibend`;
            default: return "";
        }
    }, [progressStage, elapsedSec, totalEstimateSec, model]);

    const selectedModel = MODEL_OPTIONS.find((m) => m.value === model)!;

    return (
        <div data-testid="admin-vorher-nachher-360" className="space-y-8">
            <div>
                <div className="hf-eyebrow">KI-Werkzeug · 360°</div>
                <h1 className="font-display text-3xl md:text-4xl mt-2">360°-Vorher-Nachher-Generator</h1>
                <p className="mt-1 text-hf-muted max-w-2xl">
                    Erzeuge equirektangulare 360°-Bildpaare via Blockade Labs Skybox. Perspektive und
                    Architektur werden per Control-Image-Remix erhalten.
                </p>
            </div>

            <div
                className="rounded-md p-4 text-sm flex gap-3"
                style={{ background: "hsl(var(--hf-gold-soft) / 0.25)", color: "hsl(var(--hf-ink))" }}
                data-testid="vn360-info-box"
            >
                <Info size={18} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--hf-gold-dark))" }} />
                <div>
                    360°-Generierungen sind aufwändiger als 2D. Für schnelle Iterationen nutze den
                    2D-Generator, für Präsentations-Panoramen dieses Werkzeug. Rate-Limit: 10 Paare/Tag.
                </div>
            </div>

            <section
                className="rounded-md border p-6 space-y-5"
                style={{ borderColor: "hsl(var(--hf-line))", background: "hsl(var(--hf-bg))" }}
                data-testid="vn360-form-panel"
            >
                <div>
                    <label className="hf-eyebrow" style={{ fontSize: "10px" }} htmlFor="vn360-objekt">
                        Objekt beschreiben
                    </label>
                    <input
                        id="vn360-objekt"
                        type="text"
                        value={objekt}
                        onChange={(e) => setObjekt(e.target.value)}
                        placeholder="z. B. großes Wohnzimmer mit Panoramafenstern, Pool-Innenhof, Foyer"
                        className="w-full mt-2 px-3 py-2 rounded-sm bg-white border text-sm"
                        style={{ borderColor: "hsl(var(--hf-line))" }}
                        data-testid="vn360-input-objekt"
                        disabled={loading}
                        maxLength={200}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <label className="hf-eyebrow" style={{ fontSize: "10px" }} htmlFor="vn360-vorher">
                            Vorher-Szenario
                        </label>
                        <select
                            id="vn360-vorher"
                            value={vorherSz}
                            onChange={(e) => setVorherSz(e.target.value)}
                            className="w-full px-3 py-2 rounded-sm bg-white border text-sm"
                            style={{ borderColor: "hsl(var(--hf-line))" }}
                            data-testid="vn360-select-vorher"
                            disabled={loading}
                        >
                            {VORHER_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                        </select>
                        {vorherSz === "custom" && (
                            <CustomBox
                                testIdPrefix="vn360-vorher-custom"
                                value={vorherCustom}
                                onChange={setVorherCustom}
                                placeholder="z. B. verlassener Werkstattraum mit Werkzeugregalen, viel Staub, kaltes Nordlicht"
                                disabled={loading}
                            />
                        )}
                    </div>
                    <div className="space-y-2">
                        <label className="hf-eyebrow" style={{ fontSize: "10px" }} htmlFor="vn360-nachher">
                            Nachher-Szenario
                        </label>
                        <select
                            id="vn360-nachher"
                            value={nachherSz}
                            onChange={(e) => setNachherSz(e.target.value)}
                            className="w-full px-3 py-2 rounded-sm bg-white border text-sm"
                            style={{ borderColor: "hsl(var(--hf-line))" }}
                            data-testid="vn360-select-nachher"
                            disabled={loading}
                        >
                            {NACHHER_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                        </select>
                        {nachherSz === "custom" && (
                            <CustomBox
                                testIdPrefix="vn360-nachher-custom"
                                value={nachherCustom}
                                onChange={setNachherCustom}
                                placeholder="z. B. Lounge mit warmem Kaminfeuer, Panoramafenster mit Alpenblick, warmes Abendlicht"
                                disabled={loading}
                            />
                        )}
                    </div>
                </div>

                <div>
                    <label className="hf-eyebrow" style={{ fontSize: "10px" }} htmlFor="vn360-model">
                        Qualitäts-Modell
                    </label>
                    <select
                        id="vn360-model"
                        value={model}
                        onChange={(e) => setModel(e.target.value)}
                        className="w-full mt-2 px-3 py-2 rounded-sm bg-white border text-sm"
                        style={{ borderColor: "hsl(var(--hf-line))" }}
                        data-testid="vn360-select-model"
                        disabled={loading}
                    >
                        {MODEL_OPTIONS.map((m) => (
                            <option key={m.value} value={m.value}>
                                {m.label} · {m.latency} · {m.cost}
                            </option>
                        ))}
                    </select>
                    <p className="text-[11px] text-hf-muted mt-1">
                        Höhere Qualität = längere Wartezeit und höhere Kosten pro Bildpaar.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                    <HFButton
                        onClick={() => void generate()}
                        disabled={loading || quota.used >= quota.limit}
                        data-testid="vn360-generate-btn"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                <span data-testid="vn360-progress-label">{stageLabel}</span>
                            </>
                        ) : (
                            <>
                                <Compass size={14} />
                                <span>360°-Bildpaar generieren ({selectedModel.latency})</span>
                            </>
                        )}
                    </HFButton>
                    <span className="text-xs text-hf-muted" data-testid="vn360-quota">
                        Heute verwendet: <strong>{quota.used}</strong> von {quota.limit}
                    </span>
                </div>

                {error && (
                    <div
                        className="p-3 rounded text-sm flex items-start gap-3"
                        style={{ background: "hsl(var(--hf-danger) / 0.12)", color: "hsl(var(--hf-danger))" }}
                        data-testid="vn360-error"
                        role="status" aria-live="polite"
                    >
                        <span className="flex-1">{error}</span>
                        <button
                            type="button" onClick={() => setError("")}
                            aria-label="Meldung schließen" className="shrink-0 opacity-70 hover:opacity-100 text-xs"
                        >✕</button>
                    </div>
                )}
            </section>

            {current && current.vorher && current.nachher && (
                <section data-testid="vn360-result-panel" className="space-y-4">
                    <h2 className="font-display text-xl">Ergebnis</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {(["vorher", "nachher"] as const).map((k) => {
                            const p = current[k]!;
                            return (
                                <div key={k} data-testid={`vn360-result-${k}`}>
                                    <div
                                        className="hf-eyebrow mb-2 flex items-center gap-2"
                                        style={{ color: "hsl(var(--hf-gold-dark))", fontSize: "11px" }}
                                    >
                                        <Circle size={10} />
                                        <span>{k === "vorher" ? "Vorher 360°" : "Nachher 360°"}</span>
                                    </div>
                                    <div
                                        className="rounded-md border overflow-hidden"
                                        style={{ borderColor: "hsl(var(--hf-line))", height: 320 }}
                                    >
                                        <Photo360Viewer
                                            src={p.url}
                                            testIdPrefix={`vn360-viewer-${k}`}
                                        />
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2 items-center">
                                        <HFButton
                                            onClick={() => downloadFile(p.url, `360-${k}-${current.id.slice(0, 8)}.jpg`)}
                                            data-testid={`vn360-download-${k}`}
                                        >
                                            <Download size={13} />
                                            <span>Download (equirekt. JPG)</span>
                                        </HFButton>
                                        <AiFlagToggle
                                            mediaId={p.media_id}
                                            compact
                                            testIdPrefix={`vn360-ai-flag-${k}`}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex flex-wrap gap-3 pt-2">
                        <HFButton
                            onClick={() => void generate()}
                            disabled={loading || regenerating}
                            data-testid="vn360-regenerate-both-btn"
                        >
                            <RefreshCw size={13} />
                            <span>Neu generieren (Vorher + Nachher)</span>
                        </HFButton>
                        <HFButton
                            onClick={() => void regenerateNachher()}
                            disabled={loading || regenerating}
                            data-testid="vn360-regenerate-nachher-btn"
                        >
                            {regenerating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                            <span>Nur Nachher neu generieren</span>
                        </HFButton>
                    </div>
                </section>
            )}

            <section data-testid="vn360-history-panel" className="space-y-4">
                <h2 className="font-display text-xl">Letzte 360°-Generierungen</h2>
                {history.length === 0 ? (
                    <p className="text-sm text-hf-muted" data-testid="vn360-history-empty">
                        Noch keine 360°-Generierungen.
                    </p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {history.map((h) => {
                            const vBadge = scenarioBadge(h.vorher_szenario, h.vorher_custom_text);
                            const nBadge = scenarioBadge(h.nachher_szenario, h.nachher_custom_text);
                            return (
                                <div
                                    key={h.id}
                                    className="rounded-md border p-3 space-y-2"
                                    style={{ borderColor: "hsl(var(--hf-line))", background: "hsl(var(--hf-bg))" }}
                                    data-testid={`vn360-history-item-${h.id}`}
                                    title={`${vBadge.full} → ${nBadge.full}`}
                                >
                                    <div className="grid grid-cols-2 gap-1">
                                        {h.vorher?.url && (
                                            <img src={h.vorher.url} alt="Vorher"
                                                className="w-full h-24 object-cover rounded-sm cursor-pointer"
                                                onClick={() => setCurrent(h)} />
                                        )}
                                        {h.nachher?.url && (
                                            <img src={h.nachher.url} alt="Nachher"
                                                className="w-full h-24 object-cover rounded-sm cursor-pointer"
                                                onClick={() => setCurrent(h)} />
                                        )}
                                    </div>
                                    <div className="text-xs text-hf-ink font-medium truncate">{h.objekt}</div>
                                    <div className="text-[11px] text-hf-muted italic truncate">
                                        {vBadge.label} → {nBadge.label}
                                    </div>
                                    <div className="text-[11px] text-hf-muted flex justify-between">
                                        <span>{fmtDateTime(h.created_at)}</span>
                                        <span title={MODEL_LABEL[h.model] || h.model}>{h.model.replace("model_", "M")}</span>
                                    </div>
                                    <div className="flex items-center justify-between pt-1">
                                        <button
                                            type="button" onClick={() => setCurrent(h)}
                                            className="text-[11px] underline decoration-dotted"
                                            data-testid={`vn360-history-open-${h.id}`}
                                        >Öffnen</button>
                                        <button
                                            type="button" onClick={() => void deleteEntry(h.id)}
                                            aria-label="Eintrag entfernen"
                                            className="text-hf-muted hover:text-hf-danger transition-colors"
                                            data-testid={`vn360-history-delete-${h.id}`}
                                        ><Trash2 size={14} /></button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            <p className="text-[11px] text-hf-muted pt-6" data-testid="vn360-footer-cost-hint">
                Jede Generierung erzeugt 2 equirektangulare Skybox-Panoramen. Rate-Limit: {quota.limit}/Tag.
                Nutze für schnelle Iterationen den 2D-Generator.
            </p>
        </div>
    );
}
